/**
 * Server-Side Error Handling Utilities
 *
 * This module provides utilities for error handling in Next.js server actions and server components.
 * It bridges the service layer (Result pattern) with server actions (exception-based error handling).
 *
 * Key Concepts:
 * - Service layer returns Result<T, ServiceError> (explicit error handling)
 * - Server actions use exceptions (Next.js will return HTTP 500)
 * - These utilities convert between the two patterns and capture errors to Sentry
 *
 * Core Functions:
 * - throwOnFailure() - Throw if Result is failure, return value if success
 * - withServerAction() - HOF that wraps server actions with error capture
 * - createActionError() - Create a ServiceError with a specific code
 * - handleResult() - Process a Result with a success callback
 * - toErrorResponse() - Convert error to safe response format
 *
 * Pattern: Service Layer → Result → Server Action → Exception → HTTP 500
 *
 * @example
 * ```typescript
 * // 1. Service layer returns Result
 * const result = await eventService.updateEvent(id, data);
 *
 * // 2. Server action converts Result to exception
 * const event = throwOnFailure(result, {
 *   action: 'updateEventAction',
 *   entityId: id,
 *   entityType: 'event'
 * });
 *
 * // 3. If throwOnFailure throws, Next.js returns HTTP 500 to client
 * // 4. Error is automatically captured to Sentry with context
 *
 * // Client receives:
 * // - HTTP 200 + data if successful
 * // - HTTP 500 if error (error captured in Sentry)
 * ```
 *
 * Usage Patterns:
 *
 * Pattern A: Simple update with throwOnFailure
 * ```typescript
 * export async function updateEventAction(id: string, data: unknown) {
 *   const parsed = UpdateEventSchema.parse(data);
 *   const service = createEventService(userClient);
 *   const result = await service.updateEvent(id, user.id, parsed);
 *
 *   // Convert Result to exception, captures to Sentry if failure
 *   const event = throwOnFailure(result, {
 *     action: 'updateEventAction',
 *     entityId: id,
 *     entityType: 'event',
 *   });
 *
 *   revalidatePath('/events');
 *   return { success: true, event };
 * }
 * ```
 *
 * Pattern B: With custom error handling
 * ```typescript
 * export async function createEventAction(data: unknown) {
 *   const parsed = CreateEventSchema.parse(data);
 *
 *   return handleResult(
 *     await eventService.createEvent(user.id, parsed),
 *     {
 *       action: 'createEventAction',
 *       userId: user.id,
 *       data: { name: parsed.name }
 *     },
 *     (event) => {
 *       revalidatePath('/events');
 *       return { success: true, event };
 *     }
 *   );
 * }
 * ```
 *
 * Pattern C: With optional safe response
 * ```typescript
 * export async function deleteEventAction(id: string) {
 *   try {
 *     const result = await eventService.deleteEvent(id, user.id);
 *     const event = throwOnFailure(result, {
 *       action: 'deleteEventAction',
 *       entityId: id
 *     });
 *     return { success: true };
 *   } catch (error) {
 *     // Convert error to safe response (no sensitive data)
 *     const response = toErrorResponse(error, {
 *       action: 'deleteEventAction',
 *       entityId: id
 *     });
 *     return { success: false, ...response };
 *   }
 * }
 * ```
 */

import {
  captureServerActionError,
  captureServiceError,
} from '../lib/sentry-helpers';
import type { Result } from '../result/result';
import { ServiceError } from '../result/service-error';

/**
 * Server Action Error Context
 * Provides contextual information for error handling and logging
 */
export interface ServerActionErrorContext {
  /** Action/function name for identification */
  action: string;
  /** Optional step within the action (e.g., 'validation', 'database', 'service_layer') */
  step?: string;
  /** User ID performing the action */
  userId?: string;
  /** ID of the entity being operated on */
  entityId?: string;
  /** Type of entity (e.g., 'event', 'production', 'dancer') */
  entityType?: string;
  /** Additional contextual data */
  data?: Record<string, unknown>;
  /** Index signature for compatibility with sentry-helpers */
  [key: string]: unknown;
}

/**
 * Throw on Result failure
 *
 * Converts a service layer Result to either the success value or throws an error.
 * If the result is a failure, captures the error to Sentry with context and then throws.
 * If the result is a success, returns the value.
 *
 * This is the bridge between service layer (Result pattern) and server actions
 * (exception-based error handling).
 *
 * @param result - The Result from a service layer operation
 * @param context - Error context for logging and Sentry capture
 * @returns The success value if result is successful
 * @throws {ServiceError} If result is a failure
 *
 * @example
 * ```typescript
 * export async function updateEventAction(id: string, data: unknown) {
 *   const parsed = UpdateEventSchema.parse(data);
 *
 *   const service = createEventService(userClient);
 *   const result = await service.updateEvent(id, parsed);
 *
 *   // Captures to Sentry and throws if failure
 *   const event = throwOnFailure(result, {
 *     action: 'updateEventAction',
 *     step: 'service_layer',
 *     entityId: id,
 *     entityType: 'event',
 *   });
 *
 *   revalidatePath('/admin/events');
 *   return event;
 * }
 * ```
 */
export function throwOnFailure<T>(
  result: Result<T>,
  context: ServerActionErrorContext,
): T {
  if (!result.isSuccess) {
    const error = result.getError();

    // Capture the error to Sentry with full context
    captureServiceError(error, context);

    // Throw the error so Next.js returns HTTP 500
    throw error;
  }

  return result.getValue();
}

/**
 * Wrap a server action with automatic error handling
 *
 * Higher-order function that wraps server actions to automatically capture
 * errors to Sentry and convert them to HTTP 500 responses.
 *
 * Handles:
 * - Service layer errors (Result pattern failures)
 * - Zod validation errors
 * - Unexpected runtime errors
 * - Authorization errors
 *
 * @param actionName - Name of the action for logging and Sentry tagging
 * @param handler - The async function to wrap (server action)
 * @returns Wrapped function with error handling
 *
 * @example
 * ```typescript
 * export const createEventAction = withServerAction(
 *   'createEventAction',
 *   async (data: CreateEventInput) => {
 *     const parsed = CreateEventSchema.parse(data); // Throws ZodError
 *
 *     const service = createEventService(userClient);
 *     const result = await service.createEvent(user.id, parsed);
 *
 *     if (!result.isSuccess) {
 *       // withServerAction will capture and throw this
 *       throw result.getError();
 *     }
 *
 *     return result.getValue();
 *   }
 * );
 * ```
 */
export function withServerAction<TArgs extends unknown[], TReturn>(
  actionName: string,
  handler: (...args: TArgs) => Promise<TReturn>,
): (...args: TArgs) => Promise<TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    try {
      return await handler(...args);
    } catch (error) {
      // Determine error type and capture appropriately
      const isServiceError = error instanceof ServiceError;
      const isZodError =
        error &&
        typeof error === 'object' &&
        'issues' in error &&
        Array.isArray((error as { issues: unknown }).issues);

      if (isServiceError) {
        // Service layer error - use ServiceError capture
        captureServiceError(error as ServiceError, {
          action: actionName,
          step: 'service_layer',
        });
      } else if (isZodError) {
        // Zod validation error - will be re-thrown as-is
        // Client can parse the validation error
        captureServerActionError(error, {
          action: actionName,
          step: 'validation',
        });
      } else {
        // Unexpected error
        captureServerActionError(error, {
          action: actionName,
          step: 'execution',
        });
      }

      // Always re-throw so HTTP 500 is returned to client
      throw error;
    }
  };
}

/**
 * Create a typed error with error code
 *
 * Creates a ServiceError with a specific error code for categorization.
 * Useful for creating business logic errors in server actions that need
 * proper error tracking and categorization.
 *
 * @param code - Error code for categorization (e.g., 'VALIDATION_ERROR', 'NOT_FOUND')
 * @param message - Human-readable error message
 * @param details - Additional error details for debugging
 * @returns ServiceError instance
 *
 * @example
 * ```typescript
 * export async function withdrawFundsAction(amount: number) {
 *   const account = await getAccount();
 *
 *   if (account.balance < amount) {
 *     throw createActionError(
 *       'INSUFFICIENT_FUNDS',
 *       'Account balance is insufficient',
 *       { balance: account.balance, requested: amount }
 *     );
 *   }
 *
 *   // Proceed with withdrawal
 * }
 * ```
 */
export function createActionError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): ServiceError {
  return new ServiceError(code, message, undefined, details);
}

/**
 * Handle Result pattern in server actions
 *
 * Utility for checking Result.isSuccess and handling both success and failure cases
 * with proper error capture and throwing.
 *
 * @param result - The Result to handle
 * @param context - Error context for logging
 * @param onSuccess - Callback executed if result is successful (receives the value)
 * @returns The result of onSuccess callback
 * @throws {ServiceError} If result is a failure
 *
 * @example
 * ```typescript
 * export async function processEventAction(id: string) {
 *   const service = createEventService(userClient);
 *   const result = await service.getEventById(id);
 *
 *   return handleResult(result, { action: 'processEventAction', entityId: id }, (event) => {
 *     // Process the event
 *     return { success: true, name: event.name };
 *   });
 * }
 * ```
 */
export function handleResult<T, R>(
  result: Result<T>,
  context: ServerActionErrorContext,
  onSuccess: (value: T) => R,
): R {
  if (!result.isSuccess) {
    const error = result.getError();
    captureServiceError(error, context);
    throw error;
  }

  return onSuccess(result.getValue());
}

/**
 * Handle multiple Results with early termination
 *
 * Processes multiple Results and fails fast on the first error.
 * Useful when you need to check multiple service operations in sequence.
 *
 * @param results - Array of Results to check
 * @param context - Error context for logging
 * @returns Array of success values
 * @throws {ServiceError} On first failure
 *
 * @example
 * ```typescript
 * export async function bulkProcessAction(ids: string[]) {
 *   const service = createEventService(userClient);
 *
 *   const results = await Promise.all(
 *     ids.map(id => service.getEventById(id))
 *   );
 *
 *   const events = handleResults(results, {
 *     action: 'bulkProcessAction',
 *     entityType: 'event'
 *   });
 *
 *   return events.map(event => ({ id: event.id, name: event.name }));
 * }
 * ```
 */
export function handleResults<T>(
  results: Result<T>[],
  context: ServerActionErrorContext,
): T[] {
  const values: T[] = [];

  for (const result of results) {
    if (!result.isSuccess) {
      const error = result.getError();
      captureServiceError(error, { ...context, step: 'batch_operation' });
      throw error;
    }

    values.push(result.getValue());
  }

  return values;
}

/**
 * Safe error conversion for API responses
 *
 * Converts errors to a safe response format that can be sent to clients.
 * Removes sensitive information and provides consistent error response structure.
 *
 * @param error - The error to convert
 * @param context - Error context for logging
 * @returns Safe error response object
 *
 * @example
 * ```typescript
 * export async function updateEventAction(id: string, data: unknown) {
 *   try {
 *     const parsed = UpdateEventSchema.parse(data);
 *     const service = createEventService(userClient);
 *     const result = await service.updateEvent(id, parsed);
 *
 *     if (!result.isSuccess) {
 *       const errorResponse = toErrorResponse(result.getError(), {
 *         action: 'updateEventAction',
 *         entityId: id
 *       });
 *       return { success: false, ...errorResponse };
 *     }
 *
 *     return { success: true, data: result.getValue() };
 *   } catch (error) {
 *     const errorResponse = toErrorResponse(error, { action: 'updateEventAction' });
 *     return { success: false, ...errorResponse };
 *   }
 * }
 * ```
 */
export function toErrorResponse(
  error: unknown,
  context: ServerActionErrorContext,
): {
  error: string;
  code?: string;
} {
  // Capture to Sentry
  if (error instanceof ServiceError) {
    captureServiceError(error, context);
    return {
      error: error.message,
      code: error.code,
    };
  }

  captureServerActionError(error, context);

  if (error instanceof Error) {
    return {
      error: error.message,
    };
  }

  return {
    error: 'An unexpected error occurred',
  };
}

/**
 * Assert Result success with custom error message
 *
 * Validates that a Result is successful, throwing a custom error if not.
 * Useful for assertions in the middle of operations.
 *
 * @param result - The Result to assert
 * @param context - Error context for logging
 * @param customMessage - Optional custom error message
 * @returns The success value
 * @throws {ServiceError} If result is a failure
 *
 * @example
 * ```typescript
 * export async function upgradeAccountAction(accountId: string) {
 *   const service = createAccountService(userClient);
 *
 *   // Fetch account - must succeed
 *   const getResult = await service.getAccount(accountId);
 *   const account = assert(getResult, { action: 'upgradeAccountAction' });
 *
 *   // Account exists, proceed with upgrade
 *   const upgradeResult = await service.upgrade(account.id);
 *   return assert(upgradeResult, { action: 'upgradeAccountAction', step: 'upgrade' });
 * }
 * ```
 */
export function assert<T>(
  result: Result<T>,
  context: ServerActionErrorContext,
  customMessage?: string,
): T {
  if (!result.isSuccess) {
    const error = result.getError();
    const finalError = customMessage
      ? new ServiceError(
          error.code,
          customMessage,
          error.originalError,
          error.details,
        )
      : error;

    captureServiceError(finalError, context);
    throw finalError;
  }

  return result.getValue();
}
